/**
 * THE LESSONS REGISTER — 3.19 Learning & Governance spine, L1 (pure layer).
 *
 * A generalised, reusable insight — what worked, what didn't, or a recommendation —
 * so the NEXT project starts from what the last one learned. This module is the pure
 * core: the `Lesson` shape, its taxonomy (category × kind), and the selectors that
 * split one tenant-wide collection into a project's Lessons tab and the workspace
 * library, and filter it. No store, no database: the store persists what the add
 * form produces (createLesson/editLesson/deleteLesson) and the table (migration 0172)
 * is the record.
 *
 * WORKSPACE-WIDE FROM DAY ONE: `boardId` absent means a workspace-wide lesson (no
 * project); set means it was raised on that board. The library shows them all; a
 * board's Lessons tab shows its own plus (optionally) the workspace ones.
 */

import { richHasText, type RichDoc } from './rich-text';

export type LessonCategory =
  | 'scope' | 'schedule' | 'cost' | 'risk' | 'vendor' | 'quality' | 'governance' | 'team';
/** What the lesson says: something that worked, or something that didn't. A
 *  recommendation is no longer a kind of its own — it is an OPTIONAL add-on tied
 *  to a worked/didn't lesson (see `recommendation` below). */
export type LessonKind = 'worked' | 'didnt';

/** Coerce any stored kind to a current one. A legacy 'recommendation' row (from
 *  before the 3.19 redesign) reads as 'worked', so the UI never meets a kind it
 *  has no label or tint for. */
export function asLessonKind(k: string | null | undefined): LessonKind {
  return k === 'didnt' ? 'didnt' : 'worked';
}

/** A Reveal deep-link target — where the lesson came from (a card, a CR, a decision). */
export interface LessonLink {
  kind: string;
  id: string;
  label: string;
}

/** One lesson as read back from the `lessons` table (migration 0172). */
export interface Lesson {
  id: string;
  /** The board it was raised on; absent for a workspace-wide lesson. */
  boardId?: string;
  /** The headline — what the lesson is, in a few words. Leads the card. */
  title: string;
  category: LessonCategory;
  kind: LessonKind;
  /** What happened, as rich text — a lesson may run to more than one line. */
  body: RichDoc;
  /** What to do about it next time, as rich text. OPTIONAL: absent when the
   *  lesson has no forward recommendation. Its presence drives the badge. */
  recommendation?: RichDoc;
  /** Who owns any follow-up (a name or role); optional. */
  owner?: string;
  links?: LessonLink[];
  madeBy: string;
  position?: number;
  createdAt: number;
  updatedAt?: number;
}

/** The categories in the order the add form shows them. */
export const LESSON_CATEGORIES: LessonCategory[] = [
  'scope', 'schedule', 'cost', 'risk', 'vendor', 'quality', 'governance', 'team',
];

export const LESSON_CATEGORY_LABELS: Record<LessonCategory, string> = {
  scope: 'Scope',
  schedule: 'Schedule',
  cost: 'Cost',
  risk: 'Risk',
  vendor: 'Vendor',
  quality: 'Quality',
  governance: 'Governance',
  team: 'Team',
};

export const LESSON_KINDS: LessonKind[] = ['worked', 'didnt'];

export const LESSON_KIND_LABELS: Record<LessonKind, string> = {
  worked: 'What worked',
  didnt: 'What didn’t',
};

/** A lesson carries a recommendation when its optional rich field holds real
 *  text — this is what shows the "Recommendation" badge on the card. */
export const hasRecommendation = (l: Pick<Lesson, 'recommendation'>): boolean =>
  richHasText(l.recommendation);

/** The retro-line taxonomy for the AAR's "sustain" section — worked / didn't /
 *  recommendation. DISTINCT from LessonKind: an AAR line can be a standalone
 *  recommendation, which becomes a lesson's optional recommendation when filed
 *  (see lessonFromAarLine). The lesson itself is only ever worked/didn't. */
export type AarLineKind = 'worked' | 'didnt' | 'recommendation';
export const AAR_LINE_KINDS: AarLineKind[] = ['worked', 'didnt', 'recommendation'];
export const AAR_LINE_KIND_LABELS: Record<AarLineKind, string> = {
  worked: 'What worked',
  didnt: 'What didn’t',
  recommendation: 'Recommendation',
};

/** A workspace-wide lesson has no board. */
export const isWorkspaceLesson = (l: Pick<Lesson, 'boardId'>): boolean => !l.boardId;

/** A board's own lessons — those raised on it. Newest first. */
export function lessonsForBoard(all: Lesson[], boardId: string): Lesson[] {
  return all.filter((l) => l.boardId === boardId).sort((a, b) => b.createdAt - a.createdAt);
}

/** The whole workspace library — every lesson the person can see, newest first. The
 *  tenant-wide read already scopes it (workspace lessons + board lessons they can
 *  open); this is the ordering + a single place for the library to read. */
export function libraryLessons(all: Lesson[]): Lesson[] {
  return [...all].sort((a, b) => b.createdAt - a.createdAt);
}

/** What a NEW project should "carry in" — #490, the reuse half of the learning
 *  loop (PRINCE2 "capture previous lessons"). Only lessons that carry a
 *  RECOMMENDATION (the forward, actionable ones), split into two tiers: those
 *  raised on a past project of the SAME type, then everything else (workspace-wide
 *  and other types). Newest first within each. Pure: the caller passes `typeOf`
 *  to resolve a board id to its project type, so this module stays free of store
 *  knowledge; `currentBoardId`, when given, drops the project's own lessons. */
export interface CarryInLessons {
  /** From past projects of the same type (empty when no current type is known). */
  sameType: Lesson[];
  /** Everything else worth carrying in — workspace-wide and other-type. */
  rest: Lesson[];
  /** How many lessons carry in all told (sameType + rest). */
  total: number;
}
export function carryInLessons(
  all: Lesson[],
  opts: { currentType?: string; currentBoardId?: string; typeOf: (boardId: string) => string | undefined },
): CarryInLessons {
  const norm = (s?: string) => (s ?? '').trim().toLowerCase();
  const want = norm(opts.currentType);
  const byNewest = (a: Lesson, b: Lesson) => b.createdAt - a.createdAt;
  const candidates = all
    .filter((l) => hasRecommendation(l))
    .filter((l) => !opts.currentBoardId || l.boardId !== opts.currentBoardId);
  const sameType = want
    ? candidates.filter((l) => !!l.boardId && norm(opts.typeOf(l.boardId)) === want).sort(byNewest)
    : [];
  const inSame = new Set(sameType.map((l) => l.id));
  const rest = candidates.filter((l) => !inSame.has(l.id)).sort(byNewest);
  return { sameType, rest, total: candidates.length };
}

/** Filter a lesson set by category and/or kind — the library's two facets. An
 *  absent facet does not constrain. */
export function filterLessons(
  all: Lesson[],
  facets: { category?: LessonCategory; kind?: LessonKind },
): Lesson[] {
  return all.filter(
    (l) =>
      (facets.category === undefined || l.category === facets.category) &&
      (facets.kind === undefined || l.kind === facets.kind),
  );
}

/** How many lessons sit under each category — for the library's facet counts. */
export function lessonCountsByCategory(all: Lesson[]): Record<LessonCategory, number> {
  const counts = Object.fromEntries(LESSON_CATEGORIES.map((c) => [c, 0])) as Record<LessonCategory, number>;
  for (const l of all) counts[l.category] = (counts[l.category] ?? 0) + 1;
  return counts;
}

/** A draft lesson — what the add form hands the store. `madeBy`, `createdAt` and the
 *  id are stamped by the store; `boardId` is set when raised on a project, absent for
 *  a workspace-wide one. */
export interface LessonDraft {
  boardId?: string;
  title: string;
  category: LessonCategory;
  kind: LessonKind;
  body: RichDoc;
  /** Absent when the lesson has no forward recommendation. */
  recommendation?: RichDoc;
  owner?: string;
  links?: LessonLink[];
}
